import {ServiceBase} from "../ServiceBase";
import {IAbTestingFeatureProperties} from "../../../common/interfaces/ab-testing-interface";
import {
    IAbTestingAiSortData,
    IAbTestingFeatureDataProviderService,
    IWidgetABEligibility
} from "./ab-testing-feature-data-provider.service.interface";
import {abTestingFeatureIsValid} from "../../../utils/ab-testing-feature-check";
import {
    FEATURE_NAME_CONTROL_CENTER_TOGGLE,
    FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI,
    FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID,
    FEATURE_NAME_LIST_ON_TOP,
    FEATURE_NAME_VIDEO_LIVE_STREAM,
    FEATURE_NAME_AI_SORT_GAME_LIST,
    FEATURE_NAME_STATIC_VIDEO_LIST,
    FEATURE_TRANSACTION_HISTORY_WIDGET,
    FEATURE_DEPOSIT_WIDGET,
    FEATURE_CASHOUT_WIDGET,
    FEATURE_NAME_MOBILE_HEADER_SEARCH_BUTTON,
    FEATURE_NAME_ONBOARDING_TOUR,
    FEATURE_NAME_REGISTRATION_WIDGET, FEATURE_NAME_WEBAPP_BOTTOM_NAVIGATION
} from "../../../common/const/ab-testing-names";
import {action, observable, makeObservable, computed} from 'mobx';
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import {Check} from "@sparkware/app-utils";

export class AbTestingFeatureDataProviderService extends ServiceBase implements IAbTestingFeatureDataProviderService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                this._playersLikeYouGamesListTypeId();
                this._setIsVideoLiveStreamEnabled();
                this._setIsStaticVideoListEnabled();
                this._setMobileHeaderSearchButtonEnabled();
                this._setIsWebappBottomNavigationAbEnabled();
            }, {
            fireImmediately: true
        });

        this._setDataFromAnonymousAbTesting();
    }

    @observable
    mobileHeaderSearchButtonEnabled: boolean = false;

    private _setDataFromAnonymousAbTesting(): void {
        this.services.sdk.waitForAnonymousAbTestingData().then(() => {
            this._setMobileHeaderSearchButtonEnabled();
        });
    }

    @action.bound
    private _setMobileHeaderSearchButtonEnabled(): void {
        let mobileHeaderSearchBtnFeatureData: IAbTestingFeatureProperties | undefined;
        if (this.services.user.isAuthorized) {
            mobileHeaderSearchBtnFeatureData = this._getFeature(FEATURE_NAME_MOBILE_HEADER_SEARCH_BUTTON);
        } else {
            mobileHeaderSearchBtnFeatureData = this.services.sdk.getAnonymousPlayerAbTestingDataForFeature(FEATURE_NAME_MOBILE_HEADER_SEARCH_BUTTON, true);
        }
        if (mobileHeaderSearchBtnFeatureData) {
            this.mobileHeaderSearchButtonEnabled = mobileHeaderSearchBtnFeatureData?.Properties?.featuretoggle?.toLowerCase() === "true";
        }
    }

    public async isRegistrationWidgetEnabled(): Promise<boolean> {
        await this.services.sdk.waitForAnonymousAbTestingData();    
        const featureData = this.services.sdk.getAnonymousPlayerAbTestingDataForFeature(FEATURE_NAME_REGISTRATION_WIDGET, true);
        
        return featureData?.Properties?.featuretoggle?.toString().toLowerCase() === "true";
    }

    private _getWidgetFeatureEnabled(widgetFeatureName: string): IWidgetABEligibility | null {
        const featureData = this._getFeature(widgetFeatureName);
        if (!featureData) {
            return null;
        }

        return {
            featureName: featureData.FeatureName,
            testGroup: featureData.GroupName,
            testId: featureData.TestId,
            isEligible: featureData.Properties.featuretoggle.toLowerCase() === "true"
        }
    }

    get transactionHistoryWidget(): IWidgetABEligibility | null {
        return this._getWidgetFeatureEnabled(FEATURE_TRANSACTION_HISTORY_WIDGET);
    }

    get depositWidget(): IWidgetABEligibility | null {
        return this._getWidgetFeatureEnabled(FEATURE_DEPOSIT_WIDGET);
    }

    get cashoutWidget(): IWidgetABEligibility | null {
        return this._getWidgetFeatureEnabled(FEATURE_CASHOUT_WIDGET);
    }

    private get _features(): {[featureName: string]: IAbTestingFeatureProperties} {
        return this.services.sdk.getAbTestingData() || {};
    }

    private _hasFeature(featureName: string) {
        const feature = this._getFeature(featureName);
        if (!feature || !feature.Properties) {
            return false;
        }

        return true;
    }

    private _getFeature(featureName: string): IAbTestingFeatureProperties | undefined {
        return this._features[featureName.toLowerCase()];
    }

    get aiSortGamesLists(): IAbTestingAiSortData {

        const aiSortGamesListsFeatureData = this._getFeature(FEATURE_NAME_AI_SORT_GAME_LIST);

        const result: IAbTestingAiSortData = {arenas: []};
        if (abTestingFeatureIsValid(aiSortGamesListsFeatureData, ["arenas"]) && Check.isString(aiSortGamesListsFeatureData.Properties.arenas)) {
            try {
                result.arenas = JSON.parse(aiSortGamesListsFeatureData.Properties.arenas.replace(/'/g, '"'));
            } catch (e) {
                this.services.logger.error(`Failed to parse AB Test data for feature "${FEATURE_NAME_AI_SORT_GAME_LIST}".\n${e}`);
            }
        }
        return result;
    }

    get searchGameSuggestionByAI(): string | null {
        if (this._hasFeature(FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI)) {
            const abTestingSearchGameSuggestionByAI = this._getFeature(FEATURE_NAME_SEARCH_GAME_SUGGESTED_BY_AI);
            if (abTestingFeatureIsValid(abTestingSearchGameSuggestionByAI, ["aitype"])) {
                return abTestingSearchGameSuggestionByAI.Properties?.aitype?.toLowerCase();
            }
        }

        return null;
    }

    @observable.ref
    playersLikeYouGamesListTypeId = null;


    @action.bound
    private _playersLikeYouGamesListTypeId(): void {
        if (this._hasFeature(FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID)) {
            const abTestingPlayersLikeYouGamesListTypeIdAIData = this._getFeature(FEATURE_NAME_PLAYERS_LIKE_YOU_GAMES_LIST_TYPE_ID);
            if (abTestingFeatureIsValid(abTestingPlayersLikeYouGamesListTypeIdAIData, ["listid"])) {
                this.playersLikeYouGamesListTypeId = abTestingPlayersLikeYouGamesListTypeIdAIData.Properties?.listid?.toLowerCase();
            } else {
                this.playersLikeYouGamesListTypeId = null;
            }
        } else {
            this.playersLikeYouGamesListTypeId = null;
        }
    }

    get listOnTop(): number | null {
        if (this._hasFeature(FEATURE_NAME_LIST_ON_TOP)) {
            const abTestingListOnTopData = this._getFeature(FEATURE_NAME_LIST_ON_TOP);
            if (abTestingFeatureIsValid(abTestingListOnTopData, ["listid"])) {
                return parseInt(abTestingListOnTopData.Properties?.listid);
            }
        }
        return null;
    }

    get isControlCenterEnabled(): boolean {
        if (this._hasFeature(FEATURE_NAME_CONTROL_CENTER_TOGGLE)) {
            const abTestingControlCenterData = this._getFeature(FEATURE_NAME_CONTROL_CENTER_TOGGLE);
            if (abTestingFeatureIsValid(abTestingControlCenterData, ["enablesafezone"])) {
                return abTestingControlCenterData.Properties?.enablesafezone.toLowerCase() === "true";
            }
            return false;
        }

        const currentRegulationCode = this.services.regulation.getRegulationCode();
        const enabledForRegulation = this.services.configuration.servicesConfigs.SafezoneLoader.enabledForRegulation;
        if (enabledForRegulation && Check.isBoolean(enabledForRegulation[currentRegulationCode]) && enabledForRegulation[currentRegulationCode]) {
            return enabledForRegulation[currentRegulationCode];
        } else {
            return Boolean(this.services.configuration.data.safezone?.enabled);
        }
    }

    @observable.ref
    isVideoLiveStreamEnabled: boolean = false;

    @action.bound
    _setIsVideoLiveStreamEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_VIDEO_LIVE_STREAM)) {
            const featureData = this._getFeature(FEATURE_NAME_VIDEO_LIVE_STREAM);
            if (abTestingFeatureIsValid(featureData, ["featuretoggle"])) {
                isEnabled = featureData.Properties.featuretoggle.toLowerCase() === "true";
            }
        }
        this.isVideoLiveStreamEnabled = isEnabled;
    }

    @observable.ref
    isStaticVideoListEnabled: boolean = false;

    @action.bound
    _setIsWebappBottomNavigationAbEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_WEBAPP_BOTTOM_NAVIGATION)) {
            const featureData = this._getFeature(FEATURE_NAME_WEBAPP_BOTTOM_NAVIGATION);
            if (abTestingFeatureIsValid(featureData, ["featuretoggle"])) {
                isEnabled = featureData.Properties.featuretoggle.toLowerCase() === "true";
            }
        }
        this._isBottomNavigationAbEnabled = isEnabled;
    }

    @observable
    _isBottomNavigationAbEnabled: boolean = false;

    @computed
    get isWebappBottomNavigationEnabled(): boolean {
        if (!this.services.mediaQuery.lAndBelow || this.services.miniOrbit.isEnabled) {
            return false;
        }

        if (this.services.device.hybrid.isHybrid) {
            return true;
        }

        if (Boolean(this.services.configuration.data.bottomNavigationBar?.isEnabledOnWebapp)) {
            return true;
        }

        return this._isBottomNavigationAbEnabled;
    }

    @action.bound
    _setIsStaticVideoListEnabled(): void {
        let isEnabled = false;
        if (this._hasFeature(FEATURE_NAME_STATIC_VIDEO_LIST)) {
            const featureData = this._getFeature(FEATURE_NAME_STATIC_VIDEO_LIST);
            if (abTestingFeatureIsValid(featureData, ["featuretoggle"])) {
                isEnabled = featureData.Properties.featuretoggle.toLowerCase() === "true";
            }
        }
        this.isStaticVideoListEnabled = isEnabled;
    }

    get onboardingTourEnabled(): boolean {
        if (this._hasFeature(FEATURE_NAME_ONBOARDING_TOUR)) {
            const feature = this._getFeature(FEATURE_NAME_ONBOARDING_TOUR);
            if (abTestingFeatureIsValid(feature, ["featuretoggle"])) {
                return feature.Properties.featuretoggle.toLowerCase() === "true";
            }
        }
        return false;
    }
}
